'use client'

import { RangeSlider, Text, Title } from '@mantine/core'
import { RiCloseLine, RiSearchLine } from '@remixicon/react'
import { useCurrencyPrice } from '@repo/ui'
import { useTranslations } from 'next-intl'
import { memo } from 'react'

const ALL_BEDROOMS_VALUE = 'All'

interface FiltersSectionProps {
  loading: boolean
  data: unknown
  searchProperties: string
  setSearchProperties: (value: string) => void
  selectedBedroom: string
  setSelectedBedroom: (value: string) => void
  priceRange: [number, number]
  setPriceRange: (value: [number, number]) => void
  minMaxPrice: [number, number]
  selectedPropertyType: string
  setSelectedPropertyType: (value: string) => void
  resetPriceFilter: () => void // Add this
}

function FiltersSection({
  loading,
  data,
  searchProperties,
  setSearchProperties,
  selectedBedroom,
  setSelectedBedroom,
  priceRange,
  setPriceRange,
  minMaxPrice,
  selectedPropertyType,
  setSelectedPropertyType,
  resetPriceFilter, // Add this
}: FiltersSectionProps) {
  const { displayPrice } = useCurrencyPrice()
  const t = useTranslations('propertyListing')
  const [minPrice, maxPrice] = minMaxPrice

  const bedroomOptions = [ALL_BEDROOMS_VALUE, '1', '2', '3', '4+']

  // Property type filter hidden for now — options retained for re-enable.
  // const propertyTypeOptions = [
  //   { value: 'apartment', label: t('propertyTypes.apartment'), icon: RiBuildingLine },
  //   { value: 'villa', label: t('propertyTypes.villa'), icon: RiHomeLine },
  // ]

  const getBedroomAriaLabel = (value: string) => {
    if (value === ALL_BEDROOMS_VALUE) {
      return t('filters.filterByAllBedrooms')
    }

    if (value === '1') {
      return t('filters.filterBySingleBedroom')
    }

    if (value === '4+') {
      return t('filters.filterByFourOrMoreBedrooms')
    }

    return t('filters.filterByMultipleBedrooms', { count: value })
  }

  // Clear All
  const clearAll = () => {
    setSearchProperties('')

    // Use resetPriceFilter instead of manually setting priceRange
    if (resetPriceFilter) {
      resetPriceFilter()
    } else {
      // Fallback to original behavior
      setPriceRange([0, 0])
    }

    setSelectedBedroom(ALL_BEDROOMS_VALUE)
    setSelectedPropertyType('')
  }

  return (
    <div className='w-full md:w-[280px] md:min-w-[280px] bg-white rounded-xl md:border md:border-[#EDEDED] md:shadow-sm md:p-5 md:pb-6'>
      <div className='flex justify-between items-center mb-5'>
        <Title
          order={5}
          fz={{ base: '14px', md: '15px' }}
          fw={600}
          ff='Poppins, sans-serif'
          c='#191919'
        >
          {t('filters.title')}
        </Title>
        <button
          type='button'
          onClick={clearAll}
          aria-label={t('filters.clearAllAriaLabel')}
          className='!text-[12px] text-[#898989] hover:text-[#191919] cursor-pointer transition-colors'
        >
          {t('filters.clearAll')}
        </button>
      </div>

      {/* Search input */}
      <div className='relative mb-5'>
        <RiSearchLine
          size={16}
          className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#898989] pointer-events-none'
        />

        <input
          type='text'
          placeholder={t('filters.searchPlaceholder')}
          spellCheck={false}
          className='w-full py-2.5 px-3 pl-9 pr-9 border border-[#E0E0E0] rounded-lg text-[13px] text-[#191919] font-poppins focus:outline-none focus:border-[#80c7e2] transition-colors placeholder:text-[13px] placeholder:font-poppins placeholder:text-[#898989]'
          value={searchProperties}
          onChange={(e) => setSearchProperties(e.target.value)}
        />

        <button
          type='button'
          onClick={() => setSearchProperties('')}
          aria-label={t('filters.clearSearchAriaLabel')}
          className={`absolute right-2.5 top-1/2 -translate-y-1/2 z-10 w-5 h-5 flex items-center justify-center rounded-full bg-[#E0E0E0] hover:bg-[#CACACA] transition-all ${searchProperties ? 'opacity-100 cursor-pointer' : 'opacity-0 pointer-events-none'}`}
        >
          <RiCloseLine size={13} className='text-[#333]' />
        </button>
      </div>

      {/* Price slider */}
      <div className='mb-6'>
        <Text fz='13px' fw={600} mb={8} c='#191919'>
          {t('filters.pricePerNight')}
        </Text>
        <div className='px-1'>
          <RangeSlider
            min={minPrice}
            max={maxPrice || minPrice + 1}
            label={null}
            value={priceRange}
            onChange={setPriceRange}
            disabled={loading || !data}
            classNames={{
              track: '!bg-gray-200',
              bar: '!bg-[#80c7e2]',
              thumb:
                '!border-[#80c7e2] !bg-white',
            }}
          />
        </div>
        <div className='flex justify-between items-center mt-3'>
          <span className='text-[12px] font-semibold text-[#191919] bg-[#F5F5F5] px-2.5 py-1 rounded-full'>
            {displayPrice(priceRange[0])}
          </span>
          <span className='text-[11px] text-[#898989]'>—</span>
          <span className='text-[12px] font-semibold text-[#191919] bg-[#F5F5F5] px-2.5 py-1 rounded-full'>
            {displayPrice(priceRange[1])}
          </span>
        </div>
      </div>

      {/* Number of bedrooms */}
      <div className='mb-6'>
        <Text fz='13px' fw={600} mb={8} c='#191919'>
          {t('filters.bedrooms')}
        </Text>
        <div className='grid grid-cols-5 gap-1.5'>
          {bedroomOptions?.map((num) => (
            <button
              key={num}
              type='button'
              onClick={() => setSelectedBedroom(num)}
              aria-label={getBedroomAriaLabel(num)}
              className={`h-[36px] rounded-lg text-[12px] font-medium transition-colors cursor-pointer text-center ${selectedBedroom === num
                  ? 'bg-[#191919] text-white border border-[#191919]'
                  : 'bg-[#F5F5F5] text-[#6A6B71] border border-transparent hover:bg-[#d9f0f8]'
                }`}
            >
              {num === ALL_BEDROOMS_VALUE ? t('filters.bedroomOptions.all') : num}
            </button>
          ))}
        </div>
      </div>

      {/* Property type — hidden for now */}
      {/* <div>
        <Text fz='13px' fw={600} mb={8} c='#191919'>
          {t('filters.propertyType')}
        </Text>
        <div className='grid grid-cols-2 gap-2'>
          {propertyTypeOptions.map((type) => {
            const IconComponent = type.icon
            const isSelected = selectedPropertyType === type.value
            return (
              <button
                key={type.value}
                type='button'
                onClick={() => setSelectedPropertyType(type.value)}
                aria-label={t('filters.filterByType', { type: type.label })}
                className={`flex flex-col items-center justify-center py-3 px-3 rounded-lg border transition-colors cursor-pointer ${isSelected
                    ? 'bg-[#191919] border-[#191919]'
                    : 'bg-[#F5F5F5] border-transparent hover:bg-[#d9f0f8]'
                  }`}
              >
                <IconComponent
                  size={20}
                  className={`mb-1.5 ${isSelected ? 'text-white' : 'text-[#6A6B71]'}`}
                />
                <span
                  className={`text-[11px] font-medium ${isSelected ? 'text-white' : 'text-[#6A6B71]'
                    }`}
                >
                  {type.label}
                </span>
              </button>
            )
          })}
        </div>
      </div> */}
    </div>
  )
}

export default memo(FiltersSection)
